import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, EDIT } from "const"
import { MarkovState } from "server/models"
import { StateType, deepCopy, hasAccess } from "client/utils"
import {
    PRM_MARKOV_MODEL,
    StoreType,
    MarkovStateFilters,
    MarkovRedistributionStateToList,
    MarkovRedistributionStateCalculated,

    LoadAllMarkovRedistributionStatesTo,
    DeleteMarkovRedistributionStateTo,
    SortMarkovStates,
    MarkovExpressionCalc,

    setMarkovRedistributionStateToList,
    setMarkovRedistributionStateToIds,
    setMarkovRedistributionStateToCalculatedExpressions,
    setMarkovRedistributionStateToSort,
    setMarkovRedistributionStateToFilters,
    addModal,
} from "client/store"

import {
    Container,
    Loader,
    PageForm,
    FormGroup,
    Label,
    Button,
    Table,
    TableSearchWrapper,
    SortRows,
    DeleteRow,
} from "client/components"

import MarkovStateSearch from "client/pages/search/markovStateSearch"
import EditRedistributionState from "client/pages/markov/redistributionStateTo/edit"
import Columns from "./columns"


const List = () => {
    const modelStateList = useSelector((state: StoreType) => state.markovState.stateList)
    const variableList = useSelector((state: StoreType) => state.markovVariable.variableList)
    const redistributionSelected = useSelector((state: StoreType) => state.markovRedistribution.redistributionSelected)

    const redistributionStateList = useSelector((state: StoreType) => state.markovRedistributionState.to.stateList)
    const stateIds = useSelector((state: StoreType) => state.markovRedistributionState.to.stateIds)
    const sortColumn = useSelector((state: StoreType) => state.markovRedistributionState.to.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.markovRedistributionState.to.sortOrder)
    const filters = useSelector((state: StoreType) => state.markovRedistributionState.to.filters)
    const reloadList = useSelector((state: StoreType) => state.markovRedistributionState.to.reloadList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        LoadAllMarkovRedistributionStatesTo(redistributionSelected && redistributionSelected.id).then((res) => {
            dispatch(setMarkovRedistributionStateToList(res))
            setLoadState(IDLE)
        })
    }, [redistributionSelected, reloadList])

    useEffect(() => {
        if (
            !redistributionStateList || !Object.values(redistributionStateList).length
            || !variableList || !Object.values(variableList).length
        ) return
        const calculated: MarkovRedistributionStateCalculated = {}
        Object.values(redistributionStateList).forEach((redistributionState) => {
            if (!redistributionState.expression.length) return
            const calcResult = MarkovExpressionCalc(null, redistributionState.expression, variableList, lang)
            if (calcResult.error) {
                calculated[redistributionState.stateId] = calcResult.error
            } else {
                calculated[redistributionState.stateId] = calcResult.value
            }
        })
        dispatch(setMarkovRedistributionStateToCalculatedExpressions(calculated))
    }, [redistributionStateList, variableList])

    useEffect(() => {
        const sortedIds = SortMarkovStates(Object.values(modelStateList), sortColumn, sortOrder, filters)
        const sortedIdsFiltered = sortedIds.filter((id) => !!redistributionStateList[id])
        dispatch(setMarkovRedistributionStateToIds(sortedIdsFiltered))
    }, [modelStateList, redistributionStateList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof MarkovState, order) => {
        dispatch(setMarkovRedistributionStateToSort(column, order))
    }

    const onFilter = (newFilters: MarkovStateFilters) => {
        dispatch(setMarkovRedistributionStateToFilters(newFilters))
    }

    const onDelete: DeleteRow = (state: MarkovState, callback) => {
        DeleteMarkovRedistributionStateTo(state.id, redistributionSelected.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const redistributionStateListCopy: MarkovRedistributionStateToList = redistributionStateList
                    ? deepCopy(redistributionStateList) : {}
                delete redistributionStateListCopy[state.id]
                dispatch(setMarkovRedistributionStateToList(redistributionStateListCopy))
                dispatch(setMarkovRedistributionStateToIds(stateIds.filter((id) => id !== state.id)))
            })
        })
    }

    const columns = Columns()
    const editModelAccess = hasAccess(PRM_MARKOV_MODEL, EDIT)

    return (
        <Container>
            {
                loadState === PENDING
                && <Loader />
            }
            <PageForm noPaddingBottom>
                <FormGroup>
                    <Label>{lang.statesTo}</Label>
                </FormGroup>
            </PageForm>
            <TableSearchWrapper>
                <MarkovStateSearch
                    id="redistributionStateList"
                    rightOffset={editModelAccess}
                    filters={filters}
                    onFilter={onFilter}
                />
                {
                    editModelAccess && (
                        <Button
                            variant="success"
                            onClick={() => dispatch(addModal({
                                key: "addRedistributionState",
                                content: <EditRedistributionState stateId={0} />,
                            }))}
                        >
                            {lang.add}
                        </Button>
                    )
                }
            </TableSearchWrapper>
            <Table
                columns={columns}
                dataList={modelStateList}
                dataIds={stateIds}
                sortColumn={sortColumn}
                sortOrder={sortOrder}
                rowSize="sm"
                showPagination
                scrollToTopOnPagination={false}
                onSort={onSort}
                onDelete={onDelete}
            />
        </Container>
    )
}
export default List
